import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import React, { useEffect, useState } from 'react';
import { AnimatePresence, motion, useReducedMotion } from 'framer-motion';
import { CheckCircle2, Loader2, WifiOff } from 'lucide-react';
import { getConnectionState, subscribeConnection } from '@/lib/connection-status';
/*
 * VA-29259: a small pill at the bottom centre of the recruiter app that says when
 * the device is offline or the API can't be reached, and when it's back. Bottom
 * centre so it never covers the top bar, the sidebar, the wizard's Continue button
 * (top right) or the chat launcher (bottom right). It never claims anything was
 * saved: nothing is synced here.
 */
const COPY = {
    offline: "You're offline. Changes you've made on this page are kept here until you reconnect.",
    unreachable: "Can't reach Vriti right now. We'll keep checking.",
    checking: 'Reconnecting…',
    restored: 'Connection restored',
};
export function ConnectionStatusBar() {
    const [state, setState] = useState(getConnectionState);
    const reduceMotion = useReducedMotion();
    useEffect(() => subscribeConnection(setState), []);
    const visible = state !== 'online';
    const Icon = state === 'restored' ? CheckCircle2 : state === 'checking' ? Loader2 : WifiOff;
    return (
    // The live region stays mounted so screen readers announce each change once.
    _jsx("div", { role: 'status', "aria-live": 'polite', className: 'pointer-events-none fixed inset-x-0 bottom-4 z-[1400] flex justify-center px-4', children: _jsx(AnimatePresence, { children: visible && (_jsxs(motion.div, { initial: reduceMotion ? { opacity: 0 } : { opacity: 0, y: 12 }, animate: { opacity: 1, y: 0 }, exit: reduceMotion ? { opacity: 0 } : { opacity: 0, y: 12 }, transition: { duration: 0.2, ease: 'easeOut' }, className: 'pointer-events-auto flex max-w-[min(36rem,100%)] items-center gap-2.5 rounded-full border border-white/10 bg-slate-900 px-4 py-2.5 text-[13px] font-medium leading-snug text-white shadow-[0_10px_30px_-10px_rgba(15,23,42,0.6)]', children: [_jsx(Icon, { size: 16, "aria-hidden": 'true', className: state === 'restored'
                            ? 'shrink-0 text-emerald-400'
                            : state === 'checking'
                                ? 'shrink-0 animate-spin text-sky-300'
                                : 'shrink-0 text-amber-400' }), _jsx("span", { children: COPY[state] })] }, 'connection-pill')) }) }));
}
const RECRUITER_PREFIXES = [
    '/dashboard',
    '/schedule-interview',
    '/job-description',
    '/interview-report',
    '/representative-profile',
    '/organization-profile',
    '/account-settings',
    '/my-settings',
    '/admin/console',
    '/plan',
];
/** Signed-in recruiter pages only: never the candidate interview, slot picker or public site. */
export function isRecruiterAppPath(pathname, isDashboardHome) {
    if (isDashboardHome)
        return true;
    return RECRUITER_PREFIXES.some((p) => pathname === p || pathname.startsWith(`${p}/`));
}
